import { getTheme } from '@usealloy/component-library';
import noop from 'lodash/noop';
import React, { useMemo } from 'react';

import type { FeatureFlags, Permissions, AdminPermissions } from '~/helpers/typescript';

import type { Agent } from '../application-review/ApplicationReviewTypes';
import { themes } from './ThemeProvider/constants';

interface BreadcrumbsValues {
  href: string;
  text: string;
  component?: React.ReactNode;
}

export interface Notification {
  notificationId: number;
  viewed: boolean;
  title: string;
  formattedTimestamp: string;
  timestamp: string;
  content: string;
  url?: string;
  notificationType?: string;
}

export interface UserContextValue {
  alloyEnvironment?: string;
  awsGlobalServicesRegion?: string;
  adminCustomerAccess?: { [key: string]: AdminCustomerAccess };
  agent: Agent;
  breadcrumbs?: BreadcrumbsValues[];
  customer: {
    agentId?: number;
    // This value is null for child customer if the agent is not a parent agent
    // because a child customer can have multiple parents, so a single value is only
    // relevant if the agent is a parent agent:
    afefParentAccessType?: 'controller' | 'full_audit' | 'partial_audit';
    customerId?: number;
    agentAuditEnabled?: boolean;
    organizationName?: string;
    customerAgentId?: number;
    organizationNameSlug?: string;
    vanityDomainSlug?: string;
    /**
     * If the customer is a valid AFEF Child Organization
     */
    isAfefChildOrganization?: boolean;
    /**
     * If the customer is a valid AFEF Parent Organization
     */
    isAfefParentOrganization?: boolean;
    /**
     * If the customer is a controlled AFEF child.
     * If `isAfefChildOrganization` is true, and `isControlledChild` is:
     * - False -- Customer is an autonomous child
     * - True -- Customer is a controlled child
     */
    isControlledChild?: boolean;
    /**
     * The parent customer associated with an AFEF child customer
     */
    parentCustomerId?: number;
    cidDisclaimerMessage?: string;
    /**
     * The default number of days to set for SAR reminders
     */
    sarReminderDefaultDays?: number | null;
    afefParentOrganizations?: Array<{
      organizationName: string;
      organizationNameSlug: string;
      accessType: string;
      childCustomerId: number;
      parentCustomerId: number;
    }>;
  };
  permissions: Permissions;
  adminPermissions?: AdminPermissions;
  reviewQueueEnabled: boolean;
  sessionExpireAfter: number;
  typhonDomain: string;
  featureFlags: Partial<FeatureFlags>;
  feature_flags?: Record<string, boolean | number | Array<string>>;
  updateBreadcrumbs: Function;
  getUserContext: Function;
  dromo?: {
    dromoKey: string;
    dromoOnPremKey: string;
    dromoOnPremURL: string;
  };
  userContextFetched?: boolean;
  globalServicesLambdaUrl?: string;
  provisioningLambdaUrl?: string;
  notifications?: Notification[];
  sdkAppUrl?: string;
  disableCustomerPicker?: boolean;
  isInformationalView: boolean;
  setIsInformationalView: React.Dispatch<React.SetStateAction<boolean>>;
  setEvents: React.Dispatch<React.SetStateAction<EventAllEventTypesAndStatuses[]>>;
  events: EventAllEventTypesAndStatuses[];
}

export interface AdminCustomerAccess {
  customerId: number;
  organizationNameSlug: string;
  isPermanent: boolean;
  grantedAt: string;
  startedAt: string;
  expiredAt: string;
}

type EventStatus = {
  archived: boolean;
  created_at: string;
  display_color: string;
  event_status: string;
  event_type: string;
  event_status_token: string;
  icon_name: string;
  is_alloy_owned: boolean;
  should_overwrite: boolean;
};
export interface EventAllEventTypesAndStatuses {
  event_type: string;
  event_statuses: EventStatus[];
}

const defaultUserContext = {
  adminCustomerAccess: {},
  agent: {},
  breadcrumbs: [],
  customer: {},
  featureFlags: {},
  permissions: {},
  adminPermissions: {},
  reviewQueueEnabled: false,
  sessionExpireAfter: 900,
  typhonDomain: '',
  updateBreadcrumbs: noop,
  dromo: {
    dromoKey: '',
    dromoOnPremKey: '',
    dromoOnPremURL: '',
  },
  awsGlobalServicesRegion: '',
  alloyEnvironment: '',
  provisioning_lambda_url: '',
  global_services_lambda_url: '',
  disableCustomerPicker: false,
  isInformationalView: false,
  setIsInformationalView: noop,
  setEvents: noop,
  events: [] as EventAllEventTypesAndStatuses[],
};

const defaultUserContextWithFlags = {
  ...defaultUserContext,
  feature_flags: {
    'enforce-mfa': true,
    'alert-generation-in-journeys': false,
    'alloy-forms-show-run-application': false,
    'alloy-forms-show-sdk': false,
    'alloy-lab-menu-option': false,
    'alloy-lab-popup': false,
    'api-keys-settings-page': false,
    'redirect-to-vanity': false,
    'auto-assign-by-role': false,
    'case-management': false,
    'champion-challenger-lower-minimum': false,
    'changelog-menu-option': false,
    'cm-trx-status-update-reason': false,
    'cp-sanctions-trx-fetch': false,
    'credit-panel': false,
    'unified-credit-panel': false,
    'customer-webhooks-service': false,
    'display-partner-id': false,
    'document-re-request': false,
    'entity-fraud-snapshot': false,
    'fraud-attack-radar-flag': ['disabled'],
    'enable-sign-authn-request': false,
    'eval-notes-and-docs': false,
    'expanded-credit-panel-derogatory-info': false,
    'hide-review-queue': false,
    'hide-all-csv-export-buttons': false,
    'is-table-component-available': false,
    'journey-applications-saved-search': false,
    journeys: false,
    'llm-evaluation-summary-embedded': false,
    'mid-journey-reconciliation': false,
    'portfolio-evaluations': false,
    'recon-workflow-in-graph': false,
    'rerun-app-enhancements': false,
    'rule-based-notifications': false,
    'rt-evaluation-backtest-phase': 0,
    'sanctions-review': false,
    'sar-report': false,
    'sar-report-v2': false,
    'sar-report-v3': false,
    'sdk-blocked-plugins-list': [],
    'sdk-settings-legacy': false,
    'step-up-expiration': false,
    'suggested-entities': false,
    'workflow-duplicate-node': false,
    'sdk-show-biometric-options': true,
    'settings-show-cid-configuration-field': false,
    'entity-overview-published-attributes': false,
    'run-app-prefill-pii': false,
    investigations: false,
    'investigation-analytics-dashboard': false,
    'entity-application-manual-review-outcome': false,
    'ongoing-journey-investigations-config': false,
    'rule-explainability-phase': 0,
    'workflow-cloning-rebuild-frontend': false,
    'policy-tests-services-dev-phase': 0,
    'portfolio-workflow-backtest-phase': 0,
    'sdk-custom-analytics-tool': false,
    // TODO: remove this flag once lazy loading JA detail is implemented
    'forbid-reviewing-huge-jas': false,
  },
};

export interface MessageContextValue {
  createMessage: Function;
  deleteMessage: Function;
  displayedAt: string;
  messages: unknown;
}
export interface AuthContextValue {
  isAuthenticated: boolean;
  alloyEnvironment: string;
}

const defaultAuthContext = {
  isAuthenticated: undefined,
  alloyEnvironment: undefined,
};

export interface NotificationContextValue {
  notifications: Array<Notification>;
}

const AlertColorContext = React.createContext({});
const AuthContext = React.createContext<Partial<AuthContextValue>>(defaultAuthContext);
const MessageContext = React.createContext<Partial<MessageContextValue>>({});
const NotificationsContext = React.createContext<Partial<NotificationContextValue>>(
  { notifications: [] },
);

export type Theme = ReturnType<typeof getTheme>;
const ThemeContext = React.createContext<Partial<Theme>>(
  getTheme(themes.light),
);

const UserContext = React.createContext<Partial<UserContextValue>>(defaultUserContext);

interface EntityContextValue {
  entityProfileSummaryOpen: boolean;
  // The entity token explicitly set via openEntityProfileSummary - takes priority over Redux state
  selectedEntityToken: string | null;
  toggleEntityProfileSummary(): void;
  openEntityProfileSummary(entityToken?: string): void;
  closeEntityProfileSummary(): void;
}

const defaultEntityContext = {
  entityProfileSummaryOpen: false,
  selectedEntityToken: null,
  toggleEntityProfileSummary: () => { },
  openEntityProfileSummary: () => { },
  closeEntityProfileSummary: () => { },
};

const EntityContext = React.createContext<Partial<EntityContextValue>>(defaultEntityContext);

interface EntityProviderProps {
  children: React.ReactNode;
}

const EntityProvider = ({ children }: EntityProviderProps) => {
  const { entityProfileSummaryOpen: defaultEntityProfileSummaryOpen } = defaultEntityContext;
  const [
    entityProfileSummaryOpen,
    setEntityProfileSummaryOpen,
  ] = React.useState<boolean>(defaultEntityProfileSummaryOpen);
  const [
    selectedEntityToken,
    setSelectedEntityToken,
  ] = React.useState<string | null>(null);

  const toggleEntityProfileSummary = () => {
    setEntityProfileSummaryOpen(open => !open);
  };

  /**
   * Opens the entity profile summary drawer.
   * @param entityToken - Optional entity token to display. If provided, it takes priority
   *                      over any entity token derived from Redux state.
   */
  const openEntityProfileSummary = (entityToken?: string) => {
    if (entityToken) {
      setSelectedEntityToken(entityToken);
    }
    setEntityProfileSummaryOpen(true);
  };

  const closeEntityProfileSummary = () => {
    setEntityProfileSummaryOpen(false);
    // Clear the selected entity token when closing to allow Redux state to take over next time
    setSelectedEntityToken(null);
  };

  const value = useMemo(() => ({
    entityProfileSummaryOpen,
    selectedEntityToken,
    toggleEntityProfileSummary,
    openEntityProfileSummary,
    closeEntityProfileSummary,
  }), [entityProfileSummaryOpen, selectedEntityToken]);

  return (
    <EntityContext.Provider
      value={value}
    >
      {children}
    </EntityContext.Provider>
  );
};

export {
  AlertColorContext,
  AuthContext,
  defaultAuthContext,
  defaultUserContext,
  defaultUserContextWithFlags,
  EntityContext,
  EntityProvider,
  MessageContext,
  NotificationsContext,
  ThemeContext,
  UserContext,
};
